import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Sparkles } from "lucide-react";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useGeneration, useOnline, useSettingsState } from "../hooks/bindings.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import styles from "../design-system/ui.module.css";

const SAMPLE_WORDS = ["charge", "book", "run", "seep"];

export function AddWordScreen() {
  const { generation } = useAppServices();
  const state = useGeneration();
  const settings = useSettingsState();
  const online = useOnline();
  const navigate = useNavigate();
  const [word, setWord] = useState(state.input.word);
  const [context, setContext] = useState(state.input.context ?? "");
  const sampleMode = settings.settings.aiMode === "sample";
  const canDiscover = online || sampleMode;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    generation.setInput({ word, context });
    const existing = generation.inspectExisting(word);
    if (existing.exists) {
      navigate(`/words/${existing.wordId}?from=add`);
      return;
    }
    if (!canDiscover) {
      return;
    }
    generation.beginFromInput();
    const next = generation.getSnapshot();
    if (next.phase === "error") {
      return;
    }
    navigate("/add/choose");
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title="Add New Word" backTo="/" />
        <header className={styles.pageHead}>
          <h1>
            What’s your
            <br />
            next word?
          </h1>
          <p>Find the meanings you want to learn.</p>
        </header>
        <form onSubmit={submit}>
          <label className={styles.field}>
            Word or phrase
            <input
              className={styles.input}
              value={word}
              onChange={(event) => setWord(event.target.value)}
              placeholder="e.g. charge"
              required
              autoComplete="off"
            />
          </label>
          <p className={styles.countLabel}>Try a word</p>
          <div className={styles.chips} style={{ marginTop: 8 }}>
            {SAMPLE_WORDS.map((item) => (
              <button
                key={item}
                className={styles.chip}
                type="button"
                onClick={() => setWord(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <label className={styles.field}>
            Where did you find it? <span className={styles.optional}>Optional</span>
            <textarea
              className={styles.textarea}
              value={context}
              onChange={(event) => setContext(event.target.value)}
              placeholder="Paste a sentence to help find the right meaning."
            />
          </label>
          {!canDiscover ? (
            <p className={styles.error}>Finding new meanings needs internet. Saved words still work offline.</p>
          ) : null}
          {!sampleMode && !settings.hasKey ? (
            <div className={styles.banner}>
              <div>
                No Gemini key yet. Switch Generator to Sample generator in{" "}
                <Link to="/settings">Settings</Link> to explore without an API key.
              </div>
            </div>
          ) : null}
          {state.error && state.phase === "error" ? <p className={styles.error}>{state.error.message}</p> : null}
          <div className={styles.banner}>
            <Sparkles />
            <div>Choose your meanings first. Then we’ll create a learning card for each one.</div>
          </div>
          <PrimaryButton type="submit" disabled={!word.trim() || (!canDiscover && !generation.inspectExisting(word).exists)}>
            Find meanings
          </PrimaryButton>
        </form>
      </Screen>
    </Frame>
  );
}
